import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './Scrollable.scss';
import isBrowser from '~/utils/isBrowser';

let DEVICE_HAS_MOUSE = true;

if (isBrowser) {
  DEVICE_HAS_MOUSE = (() => {
    const hasTouch = 'ontouchstart' in window;
    const anyHover = window.matchMedia('(any-hover: hover)').matches;
    const anyFine = window.matchMedia('(any-pointer: fine)').matches;

    if (window.navigator && /android/.test(window.navigator.userAgent.toLowerCase())) {
      return false;
    }

    if (hasTouch && (anyHover || anyFine)) {
      return true;
    }

    return !hasTouch;
  })();
}

export default class Scrollable extends Component {
  state = {
    height: 'auto',
    top: 0,
    right: 0,
    display: 'block',
  };

  static propTypes = {
    onUpdate: PropTypes.func,
    onScroll: PropTypes.func,
    onContentRef: PropTypes.func,
    className: PropTypes.string,
  };

  static defaultProps = {
    onUpdate() {},
    onScroll() {},
    onContentRef() {},
    className: '',
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateScrolbar);
    document.addEventListener('mousemove', this.updateScrolbar);

    this.content.addEventListener('scroll', this.updateScrolbar);
    this.scrollbar.addEventListener('mousedown', this.onDragStart);

    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onDragStop);

    this.updateScrolbar();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateScrolbar);
    document.removeEventListener('mousemove', this.updateScrolbar);

    this.content.removeEventListener('scroll', this.updateScrolbar);
    this.scrollbar.removeEventListener('mousedown', this.onDragStart);
  }

  onDragStart = (ev) => {
    this.lastPageY = ev.pageY;

    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.onDragStop);
  };

  onDrag = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    const delta = ev.pageY - this.lastPageY;
    this.lastPageY = ev.pageY;

    window.requestAnimationFrame(() => {
      this.content.scrollTop += delta / this.scrollRatio;
    });
  };

  onDragStop = () => {
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onDragStop);
  };

  updateScrolbar = () => {
    const { onUpdate } = this.props;

    const totalHeight = this.content.scrollHeight;
    const ownHeight = this.content.clientHeight;

    this.scrollRatio = ownHeight / totalHeight;

    const {
      overflow: contentOverflow,
      // eslint-disable-next-line no-undef
    } = getComputedStyle(this.content);

    if (this.scrollRatio >= 1 || contentOverflow === 'hidden') {
      this.setState({
        display: 'none',
      });

      onUpdate({
        hasScrollbar: false,
      });

      return;
    }

    this.setState({
      right: (this.container.clientWidth - this.scrollbar.clientWidth) * -1,
      height: `${Math.max(this.scrollRatio * 100, 10)}%`,
      top: `${(this.content.scrollTop / totalHeight) * 100}%`,
      display: 'block',
    });

    onUpdate({
      hasScrollbar: !!DEVICE_HAS_MOUSE,
    });
  };

  render() {
    const { children, onScroll, onUpdate, onContentRef, className, ...props } = this.props;

    const { right, height, top, display } = this.state;

    return (
      <div className={`scrollable ${className}`} onScroll={onScroll} ref={(ref) => (this.container = ref)} {...props}>
        <div className="wrapper" ref={(ref) => (this.wrapper = ref)}>
          <div
            className="scroll-content"
            ref={(ref) => {
              this.content = ref;
              onContentRef(ref);
            }}
          >
            {children}
          </div>
        </div>

        <div
          className="scrollbar-wrapper"
          style={{
            display: DEVICE_HAS_MOUSE ? display : 'none',
          }}
        >
          <div
            className="scrollbar"
            ref={(ref) => (this.scrollbar = ref)}
            style={{
              right,
              height,
              top,
            }}
          />
        </div>
      </div>
    );
  }
}
